import useNaicsSectors from "../../../shared/hooks/useNaicsSectors";
import NaicsSelector from "../../../shared/components/NaicsSelector";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import api from "../../../services/api/axios";
import SectorDropdown from "../../../shared/forms/SectorDropDown";
import StateDropdown from "../../../shared/forms/StateProvinceDropDown";
import CountryDropdown from "../../../shared/forms/CountriesDropDown";
import CountyDropdown from "../../../shared/forms/CountyDropDown";
import { useAuth } from "../../../providers/AuthProvider";
import CompanyActivityTracker from "../../../shared/components/CompanyActivityTracker";
import SaveIcon from "@mui/icons-material/Save";
import BackButton from "../../../shared/components/BackButton";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import StorageIcon from "@mui/icons-material/Storage";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Tabs,
  Tab,
  Chip,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  Divider,
  Paper,
  InputAdornment,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { PRIMARY_BUTTON_SX } from "../../../theme/buttonstyles";
import {
  buildBRECompanyPayload,
  createEmptyBRECompany,
  formatBroadSectorLabel,
  hydrateBRECompanyForm,
  PIPELINE_STAGE_OPTIONS,
} from "./BRECompanyHelpers";
import { lookupBRECompanyMatches, updateBRECompanyPipelineStage } from "./BRECompanyAPI";

import { addRecentItem } from "../../home-dashboard/RecentItemsHelper";
import BREContacts from "../contacts/BREContacts";
import BRECompanyLeaseDocumentsTab from "./BRECompanyLeaseDocumentsTab";
import BRECompanyContactsModal from "./BRECompanyContactsModal";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

const DATE_FIELD_SX = {
  "& .MuiOutlinedInput-root, & .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root": {
    color: BRAND_BLUE,
    background: "linear-gradient(180deg, rgba(231,240,218,0.25) 0%, #ffffff 80%)",
    boxShadow: "0 4px 14px rgba(0,51,108,0.12)",
    "& fieldset": { border: `2px solid ${BRAND_BLUE}` },
    "&:hover fieldset": { borderColor: BRAND_BLUE },
    "&.Mui-focused fieldset": { borderColor: BRAND_BLUE, borderWidth: 2 },
  },
  "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused, & .MuiIconButton-root": {
    color: BRAND_BLUE,
  },
};

const DATE_PICKER_OVERLAY_SX = {
  "& .MuiPickersCalendarHeader-label, & .MuiPickersDay-root, & .MuiDayCalendar-weekDay, & .MuiPickersArrowSwitcher-button": {
    color: BRAND_BLUE,
  },
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: BRAND_BLUE,
    color: "#fff",
    "&:hover, &:focus": { backgroundColor: BRAND_BLUE },
  },
  "& .MuiPickersDay-root.MuiPickersDay-today": { borderColor: BRAND_BLUE },
};

const PIPELINE_STAGE_CONFIG = {
  "Needs Outreach":      { bg: "#FFF8E1", color: "#E65100", border: "#FFB300" },
  "Engaged":             { bg: "#E3F2FD", color: "#0D47A1", border: "#1976D2" },
  "Support In Progress": { bg: "#E8F5E9", color: "#2E7D32", border: "#43A047" },
  "At Risk":             { bg: "#FFEBEE", color: "#C62828", border: "#EF5350" },
  "Out of Business":     { bg: "#F5F5F5", color: "#616161", border: "#9E9E9E" },
};

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return String(value ?? "").trim().length > 0;
};

const getCandidateSummary = (candidate) => [
  [candidate.city, candidate.state_or_province].filter(Boolean).join(", "),
  candidate.country,
  candidate.sector,
  candidate.industry_or_sector &&
  candidate.industry_or_sector.toLowerCase() !== String(candidate.sector || "").toLowerCase()
    ? candidate.industry_or_sector
    : "",
  candidate.employee_count ? `${candidate.employee_count} employees` : "",
].filter(Boolean);

const getVisibleMatchReasons = (candidate) =>
  (candidate.match_reasons || []).filter(
    (reason) =>
      reason !== "Matched by Seamless company search" &&
      reason !== "Exact company-name match from Seamless",
  );

const buildCandidatePatch = (candidate, sectorOptions) => {
  const patch = {
    name: candidate.company_name || "",
    website: candidate.website || "",
    description: candidate.description || "",
    phone_number: candidate.phone || "",
    address: candidate.address || "",
    city: candidate.city || "",
    state: candidate.state_or_province || "",
    zip_code: candidate.zip_code || "",
    country: candidate.country || "",
    linkedin: candidate.linkedin || "",
    twitter: candidate.twitter || "",
    facebook: candidate.facebook || "",
    number_of_employees:
      candidate.employee_count != null ? String(candidate.employee_count) : "",
  };

  const sector = candidate.sector || "";
  const industryOrSector = candidate.industry_or_sector || "";
  const sectorMatch = sectorOptions.find(
    (option) => option.toLowerCase() === (sector || industryOrSector).toLowerCase(),
  );

  if (sectorMatch) {
    patch.sector = sectorMatch;
  }

  if (industryOrSector) {
    patch.industry = industryOrSector;
    patch.industry_custom = industryOrSector;
    patch.naics_text = "";
  }

  if (candidate.naics_code) {
    patch.naics_code = candidate.naics_code;
    patch.naics_text = candidate.naics_name || "";
    patch.industry_custom = candidate.naics_extra?.source_classification?.naics_name || industryOrSector;
  }

  return patch;
};

const getSectionCompletion = (company, fields) => {
  const completed = fields.filter((field) =>
    hasValue(field.getValue ? field.getValue(company) : company[field.key]),
  ).length;
  return { completed, total: fields.length };
};

function SectionHeader({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: BRAND_GREEN, flexShrink: 0 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE }}>
        {children}
      </Typography>
    </Box>
  );
}

function BRECompanyDetails({
  listRoute = "/bre-companies",
  newTitle = "New Company",
  apiEndpoint = "/user-companies",
  showRelatedTabs = true,
  saveLabel = null,
} = {}) {
  const { options: sectorOptions } = useNaicsSectors();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = window.location.pathname.endsWith("/new");
  const { token, user } = useAuth();

  const [company, setCompany] = useState(createEmptyBRECompany);
  const [loading, setLoading] = useState(!isNew);
  const [tabIndex, setTabIndex] = useState(0);
  const [contactsModalOpen, setContactsModalOpen] = useState(false);
  const [contactsRefreshKey, setContactsRefreshKey] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [companyMatches, setCompanyMatches] = useState([]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const industryText =
    company.industry_custom || company.naics_text || company.industry || "";

  const sectionCompletion = React.useMemo(() => {
    const sections = {
      general: getSectionCompletion(company, [
        { key: "name" },
        { key: "phone_number" },
        { key: "website" },
        { key: "description" },
        { key: "lease_expiration" },
      ]),
      location: getSectionCompletion(company, [
        { key: "country" },
        { key: "state" },
        { key: "city" },
        { key: "county" },
        { key: "address" },
        { key: "zip_code" },
      ]),
      business: getSectionCompletion(company, [
        { key: "sector" },
        { getValue: () => industryText || company.naics_code },
        { key: "number_of_employees" },
        { key: "annual_revenue" },
        { key: "industries_served" },
        { key: "geographic_markets_covered" },
      ]),
      social: getSectionCompletion(company, [
        { key: "linkedin" },
        { key: "twitter" },
        { key: "facebook" },
      ]),
    };
    const completed = Object.values(sections).reduce(
      (sum, section) => sum + section.completed,
      0,
    );
    const total = Object.values(sections).reduce(
      (sum, section) => sum + section.total,
      0,
    );

    return {
      ...sections,
      completed,
      total,
      overall: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [company, industryText]);

  const recentCompanySnapshot = React.useMemo(() => {
    if (!company?.id) return null;
    return { id: company.id, name: company.name, city: company.city, state: company.state };
  }, [company.id, company.name, company.city, company.state]);

  useEffect(() => {
    if (!isNew && token) {
      api
        .get(`${apiEndpoint}/${id}`)
        .then((res) => setCompany(hydrateBRECompanyForm(res.data)))
        .catch(() => showSnackbar("Failed to load company details.", "error"))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [apiEndpoint, id, isNew, token]);

  useEffect(() => {
    if (!loading && !isNew && user?.id && recentCompanySnapshot) {
      addRecentItem(user.id, "company", recentCompanySnapshot);
    }
  }, [loading, isNew, user?.id, recentCompanySnapshot]);

  const handleChange = (field) => (e) => setCompany({ ...company, [field]: e.target.value });

  const handleStateChange = (e) => {
    const nextState = e.target.value;
    setCompany((prev) => ({
      ...prev,
      state: nextState,
      county: nextState === prev.state ? prev.county : "",
    }));
  };

  const handleCountryChange = (e) => {
    const nextCountry = e.target.value;
    setCompany((prev) => ({
      ...prev,
      country: nextCountry,
      county: nextCountry === prev.country ? prev.county : "",
    }));
  };

  const handlePipelineStageChange = (stage) => {
    updateBRECompanyPipelineStage(id, stage, apiEndpoint)
      .then(() => setCompany((prev) => ({ ...prev, pipeline_stage: stage })))
      .catch(() => showSnackbar("Failed to update company status.", "error"));
  };

  const handleSubmit = () => {
    const payload = buildBRECompanyPayload(company, isNew);
    const request = isNew
      ? api.post(`${apiEndpoint}/`, payload)
      : api.patch(`${apiEndpoint}/${id}`, payload);

    request
      .then(() => navigate(listRoute))
      .catch((err) => {
        const detail = err.response?.data?.detail;
        let message = "Failed to save company. Please try again.";
        if (detail) {
          if (Array.isArray(detail)) {
            message = detail
              .map((d) => {
                const loc = Array.isArray(d.loc) ? d.loc.join(".") : d.loc || "field";
                return `${loc}: ${d.msg}`;
              })
              .join("\n");
          } else if (typeof detail === "string") {
            message = detail;
          } else {
            message = JSON.stringify(detail);
          }
        }
        showSnackbar(message, "error");
      });
  };

  const handleFindCompanyMatches = () => {
    const companyName = company.name?.trim();
    const website = company.website?.trim();

    if (!companyName && !website) {
      showSnackbar("Enter a company name or website before finding matches.", "warning");
      return;
    }

    setLookupLoading(true);
    setCompanyMatches([]);
    setMatchDialogOpen(true);

    lookupBRECompanyMatches({ companyName, website })
      .then((res) => {
        const matches = [
          ...(res.data?.final_matches || []),
          ...(res.data?.local_matches || []),
        ];
        setCompanyMatches(matches);
      })
      .catch((err) => {
        const message = err.response?.data?.detail || "Failed to find matching companies.";
        showSnackbar(Array.isArray(message) ? "Failed to find matching companies." : message, "error");
        setMatchDialogOpen(false);
      })
      .finally(() => setLookupLoading(false));
  };

  const handleApplyCompanyMatch = (candidate) => {
    const patch = buildCandidatePatch(candidate, sectorOptions);
    const fieldsToApply = Object.entries(patch).filter(
      ([field, value]) => hasValue(value) && !hasValue(company[field]),
    );

    setCompany((prev) => ({
      ...prev,
      ...Object.fromEntries(fieldsToApply),
    }));

    setMatchDialogOpen(false);
    showSnackbar(
      fieldsToApply.length > 0
        ? `Populated ${fieldsToApply.length} missing field${fieldsToApply.length === 1 ? "" : "s"}.`
        : "No empty fields were available to populate.",
      fieldsToApply.length > 0 ? "success" : "info",
    );
  };

  const handleCompanyUpdated = (nextCompany) => setCompany(hydrateBRECompanyForm(nextCompany));

  const handleExternalDbToggle = (checked) => {
    api
      .patch(`${apiEndpoint}/${id}`, { added_to_external_db: checked })
      .then((res) => setCompany((prev) => hydrateBRECompanyForm({ ...prev, ...res.data })))
      .catch(() => showSnackbar("Failed to update external database flag.", "error"));
  };

  if (loading) return (
    <Box sx={{ p: 4, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 600 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2, minHeight: "100%", boxSizing: "border-box", overflowX: "hidden", bgcolor: "#f0f4f8" }}>
      <Box sx={{ maxWidth: 1850, mx: "auto", width: "100%" }}>

        {/* HEADER CARD */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid rgba(0,51,108,0.10)",
            boxShadow: "0 2px 12px rgba(0,51,108,0.08)",
            bgcolor: "white",
          }}
        >
          <Box sx={{ height: 4, background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_GREEN})` }} />

          <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
              <BackButton to={listRoute} />
              {user?.organization?.name && (
                <Chip
                  label={user.organization.name}
                  size="small"
                  sx={{
                    bgcolor: "rgba(0,51,108,0.06)",
                    color: BRAND_BLUE,
                    fontWeight: 600,
                    fontSize: 12,
                    border: "1px solid rgba(0,51,108,0.12)",
                  }}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 2,
                  bgcolor: "rgba(0,51,108,0.07)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <BusinessIcon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ color: BRAND_BLUE, fontWeight: 700, lineHeight: 1.2 }}>
                  {isNew ? newTitle : company.name || "Untitled Company"}
                </Typography>
                {!isNew && (company.city || company.state) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                    <LocationOnIcon sx={{ fontSize: 14, color: "#d32f2f" }} />
                    <Typography variant="body2" sx={{ color: BRAND_BLUE }}>
                      {[company.city, company.state].filter(Boolean).join(", ")}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Pipeline stage chips + external DB */}
            {!isNew && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}
                  >
                    Status
                  </Typography>
                  {PIPELINE_STAGE_OPTIONS.map((stage) => {
                    const cfg = PIPELINE_STAGE_CONFIG[stage];
                    const isActive = company.pipeline_stage === stage;
                    return (
                      <Chip
                        key={stage}
                        label={stage}
                        size="small"
                        onClick={() => handlePipelineStageChange(isActive ? "" : stage)}
                        sx={{
                          cursor: "pointer",
                          fontWeight: isActive ? 700 : 500,
                          fontSize: 12,
                          bgcolor: isActive ? cfg.bg : "transparent",
                          color: isActive ? cfg.color : "text.secondary",
                          border: `1.5px solid ${isActive ? cfg.border : "rgba(0,0,0,0.15)"}`,
                          transition: "all 0.15s ease",
                          "&:hover": {
                            bgcolor: cfg.bg,
                            color: cfg.color,
                            border: `1.5px solid ${cfg.border}`,
                          },
                        }}
                      />
                    );
                  })}
                </Box>

                <Divider orientation="vertical" flexItem />

                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={!!company.added_to_external_db}
                      onChange={(e) => handleExternalDbToggle(e.target.checked)}
                      color="success"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <StorageIcon
                        sx={{
                          fontSize: 15,
                          color: company.added_to_external_db ? BRAND_GREEN : "text.secondary",
                        }}
                      />
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: company.added_to_external_db ? BRAND_GREEN : "text.secondary",
                          fontSize: 13,
                        }}
                      >
                        External DB
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
          </Box>

          {/* Tab bar */}
          <Box sx={{ px: 3, borderTop: "1px solid rgba(0,51,108,0.08)" }}>
            <Tabs
              value={tabIndex}
              onChange={(_e, i) => setTabIndex(i)}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 14,
                  minHeight: 44,
                  color: "text.secondary",
                  "&.Mui-selected": { color: BRAND_BLUE },
                },
                "& .MuiTabs-indicator": { backgroundColor: BRAND_GREEN, height: 3, borderRadius: "3px 3px 0 0" },
              }}
            >
              <Tab label="Company Details" />
              {showRelatedTabs && !isNew && <Tab label="Activity" />}
              {showRelatedTabs && !isNew && <Tab label="Contacts" />}
              {showRelatedTabs && !isNew && <Tab label="Lease Documents" />}
            </Tabs>
          </Box>
        </Paper>

        {/* TAB CONTENT CARD */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid rgba(0,51,108,0.10)",
            boxShadow: "0 2px 12px rgba(0,51,108,0.08)",
            p: 3,
            bgcolor: "white",
          }}
        >
          {/* COMPANY DETAILS TAB */}
          {tabIndex === 0 && (
            <Box>
              <Box
                sx={{
                  mb: 2.5,
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  border: "1px solid rgba(0,51,108,0.06)",
                  bgcolor: "rgba(0,51,108,0.025)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 900, lineHeight: 1.15 }}>
                      Company Profile
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                      {sectionCompletion.completed} of {sectionCompletion.total} fields completed
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={handleFindCompanyMatches}
                      disabled={lookupLoading}
                      sx={{
                        borderColor: BRAND_BLUE,
                        color: BRAND_BLUE,
                        fontWeight: 800,
                        textTransform: "none",
                        "&:hover": {
                          borderColor: BRAND_BLUE,
                          bgcolor: "rgba(0,51,108,0.06)",
                        },
                      }}
                    >
                      Find Matches
                    </Button>
                    <Chip
                      label={`${sectionCompletion.overall}% Complete`}
                      sx={{
                        bgcolor: "rgba(100,131,21,0.12)",
                        color: BRAND_GREEN,
                        border: "1px solid rgba(100,131,21,0.25)",
                        fontWeight: 900,
                      }}
                    />
                  </Stack>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={sectionCompletion.overall}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: "rgba(0,51,108,0.10)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      bgcolor: BRAND_GREEN,
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
                  gap: 2,
                }}
              >
                {/* General */}
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
                <SectionHeader>General</SectionHeader>
                <Stack spacing={3}>
                  <TextField label="Company Name" value={company.name} onChange={handleChange("name")} fullWidth />
                  <TextField label="Website" value={company.website} onChange={handleChange("website")} fullWidth />
                  <TextField label="Phone" value={company.phone_number} onChange={handleChange("phone_number")} fullWidth />
                  <TextField
                    label="Description"
                    value={company.description}
                    onChange={handleChange("description")}
                    fullWidth
                    multiline
                    rows={4}
                    sx={{ "& .MuiInputBase-inputMultiline": { height: "92px !important", overflowY: "auto", resize: "none" } }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Lease Expiration"
                      value={company.lease_expiration ? dayjs(company.lease_expiration) : null}
                      onChange={(value) => setCompany((current) => ({
                        ...current,
                        lease_expiration: value?.isValid?.() ? value.format("YYYY-MM-DD") : "",
                      }))}
                      format="MM/DD/YYYY"
                      slotProps={{
                        textField: { fullWidth: true, size: "small", sx: DATE_FIELD_SX },
                        popper: { sx: DATE_PICKER_OVERLAY_SX },
                        dialog: { sx: DATE_PICKER_OVERLAY_SX },
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Box>

              {/* Location */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
                <SectionHeader>Location</SectionHeader>
                <Stack spacing={3}>
                  <CountryDropdown label="Country" value={company.country} onChange={handleCountryChange} fullWidth />
                  <StateDropdown label="State / Province" country={company.country} value={company.state} onChange={handleStateChange} fullWidth />
                  <TextField label="City" value={company.city || ""} onChange={handleChange("city")} fullWidth />
                  {String(company.country || "").trim().toLowerCase() === "united states" && (
                    <CountyDropdown label="County" country={company.country} state={company.state} value={company.county || ""} onChange={handleChange("county")} />
                  )}
                  <TextField label="Address" value={company.address || ""} onChange={handleChange("address")} fullWidth />
                  <TextField label="Zip Code" value={company.zip_code || ""} onChange={handleChange("zip_code")} fullWidth />
                </Stack>
              </Box>

              {/* Business Details */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
                <SectionHeader>Business Details</SectionHeader>
                <Stack spacing={3}>
                  <SectorDropdown
                    label="Sector"
                    value={company.sector}
                    onChange={handleChange("sector")}

                    formatOptionLabel={formatBroadSectorLabel}
                    sx={{
                      "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused, & .MuiInputBase-input, & .MuiSelect-icon": {
                        color: BRAND_BLUE,
                      },
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: { "& .MuiMenuItem-root": { color: BRAND_BLUE } },
                        },
                      },
                    }}
                    fullWidth
                  />
                  <TextField label="NAICS Text" value={company.naics_text || ""} onChange={handleChange("naics_text")} fullWidth />
                  {apiEndpoint === "/user-companies" ? (
                    <TextField label="NAICS Code" value={company.naics_code || ""} onChange={handleChange("naics_code")} fullWidth />
                  ) : (
                    <>
                      <TextField label="Custom Industry" value={company.industry_custom || ""} onChange={handleChange("industry_custom")} fullWidth />
                      <NaicsSelector value={company.naics_code || ""} title={company.naics_text || ""}
                        onChange={(code, title) => setCompany((prev) => ({ ...prev, naics_code: code, naics_text: title }))}
                        onSectorChange={(sector) => setCompany((prev) => ({ ...prev, sector }))} />
                    </>
                  )}
                  <TextField label="Number of Employees" value={company.number_of_employees || ""} onChange={handleChange("number_of_employees")} fullWidth type="number" />
                  <TextField label="Annual Revenue" value={company.annual_revenue || ""} onChange={handleChange("annual_revenue")} fullWidth type="number" />
                  <TextField label="Industries Served" value={company.industries_served || ""} onChange={handleChange("industries_served")} fullWidth />
                  <TextField label="Geographic Markets Covered" value={company.geographic_markets_covered || ""} onChange={handleChange("geographic_markets_covered")} fullWidth />
                </Stack>
              </Box>

              {/* Social Media */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
                <SectionHeader>Social Media</SectionHeader>
                <Stack spacing={3}>
                  <TextField
                    label="LinkedIn"
                    value={company.linkedin || ""}
                    onChange={handleChange("linkedin")}
                    fullWidth
                    InputProps={{ startAdornment: <InputAdornment position="start"><LinkedInIcon sx={{ color: "#0A66C2", fontSize: 20 }} /></InputAdornment> }}
                  />
                  <TextField
                    label="Twitter / X"
                    value={company.twitter || ""}
                    onChange={handleChange("twitter")}
                    fullWidth
                    InputProps={{ startAdornment: <InputAdornment position="start"><TwitterIcon sx={{ color: "#1DA1F2", fontSize: 20 }} /></InputAdornment> }}
                  />
                  <TextField
                    label="Facebook"
                    value={company.facebook || ""}
                    onChange={handleChange("facebook")}
                    fullWidth
                    InputProps={{ startAdornment: <InputAdornment position="start"><FacebookIcon sx={{ color: "#1877F2", fontSize: 20 }} /></InputAdornment> }}
                  />
                </Stack>
              </Box>
              </Box>
            </Box>
          )}

          {/* ACTIVITY TAB */}
          {showRelatedTabs && !isNew && tabIndex === 1 && id && !isNaN(id) && (
            <Box sx={{ width: "100%" }}>
              <CompanyActivityTracker companyId={Number(id)} maxHeight="75vh" />
            </Box>
          )}

          {/* CONTACTS TAB */}
          {showRelatedTabs && !isNew && tabIndex === 2 && (
            <Stack spacing={2} sx={{ width: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => setContactsModalOpen(true)}
                  sx={PRIMARY_BUTTON_SX}
                >
                  Find Contacts
                </Button>
              </Box>
              <BREContacts embedded companyId={id} refreshKey={contactsRefreshKey} />
            </Stack>
          )}

          {/* LEASE DOCUMENTS TAB */}
          {showRelatedTabs && !isNew && tabIndex === 3 && (
            <BRECompanyLeaseDocumentsTab
              companyId={id}
              company={company}
              onCompanyUpdated={handleCompanyUpdated}
            />
          )}

          {/* SAVE BUTTON */}
          {tabIndex !== 1 && tabIndex !== 2 && tabIndex !== 3 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
                pt: 2.5,
                borderTop: "1px solid rgba(0,51,108,0.08)",
              }}
            >
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                sx={PRIMARY_BUTTON_SX}
              >
                {saveLabel || (isNew ? "Add Company" : "Save Changes")}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)} variant="filled">
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <Dialog
        open={matchDialogOpen}
        onClose={() => setMatchDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: "1px solid rgba(0,51,108,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: BRAND_BLUE,
            fontWeight: 900,
            borderBottom: "1px solid rgba(0,51,108,0.10)",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <AutoAwesomeIcon sx={{ color: BRAND_GREEN }} />
          Matching Companies
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
          {lookupLoading ? (
            <Box sx={{ py: 7, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
              <CircularProgress size={24} sx={{ color: BRAND_BLUE }} />
              <Typography sx={{ color: BRAND_BLUE, fontWeight: 700 }}>Finding matches...</Typography>
            </Box>
          ) : companyMatches.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>No matching companies found.</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                Try a website domain or a more complete company name.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0} divider={<Divider />} sx={{ maxHeight: "62vh", overflowY: "auto" }}>
              {companyMatches.map((candidate) => {
                const summary = getCandidateSummary(candidate);
                const matchReasons = getVisibleMatchReasons(candidate);

                return (
                  <Box
                    key={candidate.candidate_id}
                    sx={{
                      p: 2.25,
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
                      gap: 2,
                      alignItems: "start",
                      "&:hover": { bgcolor: "rgba(0,51,108,0.025)" },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", mb: 0.75 }}>
                        <Typography sx={{ color: BRAND_BLUE, fontWeight: 900, fontSize: 16 }}>
                          {candidate.company_name || "Unnamed company"}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${candidate.confidence_score}% match`}
                          sx={{
                            bgcolor: "rgba(100,131,21,0.12)",
                            color: BRAND_GREEN,
                            border: "1px solid rgba(100,131,21,0.25)",
                            fontWeight: 800,
                          }}
                        />
                      </Stack>

                      {candidate.website && (
                        <Typography variant="body2" sx={{ color: BRAND_BLUE, mb: 0.5, overflowWrap: "anywhere" }}>
                          {candidate.website}
                        </Typography>
                      )}

                      {summary.length > 0 && (
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                          {summary.join(" | ")}
                        </Typography>
                      )}

                      {matchReasons.length > 0 && (
                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75 }}>
                          {matchReasons.slice(0, 3).map((reason) => (
                            <Chip
                              key={reason}
                              size="small"
                              label={reason}
                              sx={{ bgcolor: "#f5f7fa", color: "text.secondary", maxWidth: "100%" }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>

                    <Button
                      variant="contained"
                      onClick={() => handleApplyCompanyMatch(candidate)}
                      sx={{ ...PRIMARY_BUTTON_SX, whiteSpace: "nowrap" }}
                    >
                      Populate Empty Fields
                    </Button>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid rgba(0,51,108,0.10)" }}>
          <Button onClick={() => setMatchDialogOpen(false)} sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {showRelatedTabs && !isNew ? (
        <BRECompanyContactsModal
          open={contactsModalOpen}
          onClose={() => setContactsModalOpen(false)}
          companyId={id}
          companyName={company.name}
          onContactsChanged={() => setContactsRefreshKey((current) => current + 1)}
        />
      ) : null}
    </Box>
  );
}

export default BRECompanyDetails;
